import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReportTable from "../../../../common/ReportTable/ReportTable";
import Dropdown from "../../../../common/dropDown";
import { renderReceiptStatusBadge } from "../../epsfBadges";
import { findOption, formatTxnWhen, renderImpProductCell } from "../../epsfLedgerUi";
import {
  EXPIRING_SOON_OPTIONS,
  batchKey,
  filterBalanceRows,
  filterLabel,
  formatExpiryDisplay,
  formatQtyWithUnit,
  productKey,
  searchInventoryRows,
  sortBalanceRows,
  trackingLabel,
  trackingMethod,
} from "./inventoryDashboardUtils";

const BALANCE_FIELD_OPTIONS = [
  { key: "Product", label: "IMP" },
  { key: "BatchNumber", label: "Batch / Lot" },
  { key: "ExpiryDate", label: "Expiry" },
  { key: "AvailableOnHand", label: "Available" },
  { key: "HeldQuantity", label: "On hold" },
  { key: "ReturnedQuantity", label: "Returned" },
  { key: "OnHand", label: "Ledger total" },
  { key: "Tracking", label: "Tracking" },
  { key: "Storage", label: "Storage" },
  { key: "Status", label: "Status" },
];

const BALANCE_DEFAULT_COLUMNS = [
  "Product",
  "BatchNumber",
  "ExpiryDate",
  "AvailableOnHand",
  "HeldQuantity",
  "Storage",
  "Status",
];

const KIT_FIELD_OPTIONS = [
  { key: "Product", label: "IMP" },
  { key: "KitNumber", label: "Kit number" },
  { key: "BatchNumber", label: "Batch" },
  { key: "ExpiryDate", label: "Expiry" },
  { key: "Status", label: "Status" },
  { key: "Storage", label: "Storage" },
  { key: "ReceiptReference", label: "Receipt ref" },
];

const KIT_DEFAULT_COLUMNS = [
  "Product",
  "KitNumber",
  "BatchNumber",
  "ExpiryDate",
  "Status",
  "Storage",
];

const HELD_FIELD_OPTIONS = [
  { key: "Product", label: "IMP" },
  { key: "TxnAt", label: "Date" },
  { key: "Source", label: "Source" },
  { key: "BatchNumber", label: "Batch" },
  { key: "Quantity", label: "Qty" },
  { key: "ReviewReason", label: "Hold reason" },
  { key: "ReceiptStatus", label: "Status" },
  { key: "Actions", label: "Actions" },
];

const HELD_DEFAULT_COLUMNS = [
  "Product",
  "TxnAt",
  "Source",
  "BatchNumber",
  "Quantity",
  "ReviewReason",
  "ReceiptStatus",
  "Actions",
];

const TXN_FIELD_OPTIONS = [
  { key: "Product", label: "IMP" },
  { key: "TxnAt", label: "When" },
  { key: "TxnType", label: "Type" },
  { key: "BatchNumber", label: "Batch" },
  { key: "Quantity", label: "Qty" },
  { key: "ReferenceNo", label: "Reference" },
  { key: "ReceiptStatus", label: "Status" },
];

const TXN_DEFAULT_COLUMNS = [
  "Product",
  "TxnAt",
  "TxnType",
  "BatchNumber",
  "Quantity",
  "ReferenceNo",
  "ReceiptStatus",
];

const STATUS_OPTIONS = [
  { value: "all", label: "Status: All" },
  { value: "available", label: "Available" },
  { value: "held", label: "Hold / review" },
  { value: "expiring", label: "Expiring soon" },
  { value: "expired", label: "Expired" },
  { value: "recalled", label: "Recalled" },
];

const TRACKING_OPTIONS = [
  { value: "", label: "Tracking: All" },
  { value: "BatchQuantity", label: "Batch / Quantity" },
  { value: "IndividualKit", label: "Individual Kit" },
];

const SORT_OPTIONS = [
  { value: "product", label: "Sort: IMP name" },
  { value: "expiry", label: "Sort: Expiry" },
];

function productCell(row) {
  return renderImpProductCell(row);
}

function storageCell(row) {
  const code = row.StorageUnitCode || row.storageUnitCode || "";
  const name = row.StorageUnitName || row.storageUnitName || "";
  return [code, name].filter(Boolean).join(" — ") || "—";
}

export default function InventoryDetailModal({
  open,
  onClose,
  title,
  contextLabel,
  filterKey,
  expiringDays,
  impProductId,
  impGroup,
  balances,
  held,
  kits,
  kitsBusy,
  movements,
  movementsBusy,
  view,
  onViewChange,
  canReceive,
  releaseBusyId,
  onRelease,
  onClearFilter,
  onClearImp,
  onFilterKeyChange,
}) {
  const closeRef = useRef(null);
  const previouslyFocused = useRef(null);
  const [search, setSearch] = useState("");
  const [tracking, setTracking] = useState("");
  const [sortKey, setSortKey] = useState("product");
  const [localExpiringDays, setLocalExpiringDays] = useState(expiringDays);

  useEffect(() => {
    if (!open) return;
    setSearch("");
    setTracking("");
    setSortKey("product");
    setLocalExpiringDays(expiringDays);
  }, [open, filterKey, impProductId, expiringDays]);

  useEffect(() => {
    if (!open) return undefined;
    previouslyFocused.current = document.activeElement;
    const t = window.setTimeout(() => closeRef.current?.focus?.(), 0);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.clearTimeout(t);
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
      if (
        previouslyFocused.current &&
        typeof previouslyFocused.current.focus === "function"
      ) {
        previouslyFocused.current.focus();
      }
    };
  }, [open, onClose]);

  const days = Number(localExpiringDays) || expiringDays;

  const scopedBalances = useMemo(() => {
    let rows = filterBalanceRows(balances, filterKey, days);
    if (impProductId) {
      const id = String(impProductId).toLowerCase();
      rows = rows.filter((r) => productKey(r) === id);
    }
    if (tracking) {
      rows = rows.filter((r) => trackingMethod(r) === tracking);
    }
    rows = searchInventoryRows(rows, search);
    return sortBalanceRows(rows, sortKey);
  }, [balances, filterKey, days, impProductId, tracking, search, sortKey]);

  const scopedKits = useMemo(() => {
    let rows = kits || [];
    if (impProductId) {
      const id = String(impProductId).toLowerCase();
      rows = rows.filter((r) => productKey(r) === id);
    }
    return searchInventoryRows(rows, search);
  }, [kits, impProductId, search]);

  const scopedHeld = useMemo(() => {
    let rows = held || [];
    if (impProductId) {
      const id = String(impProductId).toLowerCase();
      rows = rows.filter((r) => productKey(r) === id);
    }
    return searchInventoryRows(rows, search);
  }, [held, impProductId, search]);

  const scopedMovements = useMemo(() => {
    let rows = movements || [];
    if (impProductId) {
      const id = String(impProductId).toLowerCase();
      rows = rows.filter((r) => productKey(r) === id);
    }
    return searchInventoryRows(rows, search);
  }, [movements, impProductId, search]);

  const showKitsTab =
    !impGroup || trackingMethod(impGroup) === "IndividualKit"
      ? impGroup
        ? trackingMethod(impGroup) === "IndividualKit"
        : (kits || []).length > 0 ||
          scopedBalances.some((r) => trackingMethod(r) === "IndividualKit")
      : false;

  const activeFilterLabel = filterLabel(filterKey, days);
  const expiringOptions = EXPIRING_SOON_OPTIONS.map((o) => ({
    value: String(o.value),
    label: `Expiry: ${o.label}`,
  }));

  const renderBalanceCell = useCallback((row, field) => {
    if (field === "Product") return productCell(row);
    if (field === "BatchNumber") return row.BatchNumber || row.batchNumber || "—";
    if (field === "ExpiryDate") {
      return formatExpiryDisplay(row.ExpiryDate || row.expiryDate);
    }
    if (field === "OnHand") {
      return formatQtyWithUnit(row.OnHand ?? row.onHand, row);
    }
    if (field === "AvailableOnHand") {
      return formatQtyWithUnit(row.AvailableOnHand ?? row.availableOnHand, row);
    }
    if (field === "HeldQuantity") {
      const v = row.HeldQuantity ?? row.heldQuantity;
      if (v == null || v === "" || Number(v) === 0) return "—";
      return formatQtyWithUnit(v, row);
    }
    if (field === "ReturnedQuantity") {
      const v = row.ReturnedQuantity ?? row.returnedQuantity;
      if (v == null || v === "" || Number(v) === 0) return "—";
      return formatQtyWithUnit(v, row);
    }
    if (field === "Tracking") return trackingLabel(trackingMethod(row));
    if (field === "Storage") return storageCell(row);
    if (field === "Status") {
      const status = row.Status || row.status;
      if (status) return String(status);
      return row.IsRecalled || row.isRecalled ? "Recalled" : "Active";
    }
    const value = row[field];
    return value == null || value === "" ? "—" : String(value);
  }, []);

  const renderKitCell = useCallback((row, field) => {
    if (field === "KitNumber") return row.KitNumber || row.kitNumber || "—";
    if (field === "Product") return productCell(row);
    if (field === "BatchNumber") return row.BatchNumber || row.batchNumber || "—";
    if (field === "ExpiryDate") {
      return formatExpiryDisplay(row.ExpiryDate || row.expiryDate);
    }
    if (field === "Storage") return storageCell(row);
    if (field === "Status") return row.Status || row.status || "—";
    if (field === "ReceiptReference") {
      return row.ReceiptReference || row.receiptReference || "—";
    }
    const value = row[field];
    return value == null || value === "" ? "—" : String(value);
  }, []);

  const renderHeldCell = useCallback(
    (row, field) => {
      if (field === "TxnAt") return formatTxnWhen(row) || "—";
      if (field === "Source") {
        const t = String(row.TxnType || row.txnType || "Receipt");
        return t === "Return" ? "Return" : "Receipt";
      }
      if (field === "Product") return productCell(row);
      if (field === "BatchNumber") return row.BatchNumber || row.batchNumber || "—";
      if (field === "Quantity") {
        const v = row.Quantity ?? row.quantity;
        return v == null ? "—" : String(v);
      }
      if (field === "ReviewReason") {
        return row.ReviewReason || row.reviewReason || "—";
      }
      if (field === "ReceiptStatus") {
        return renderReceiptStatusBadge(row.ReceiptStatus || row.receiptStatus);
      }
      if (field === "Actions") {
        const txnId = String(row.ImpTxnId || row.impTxnId || "");
        return (
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            disabled={!canReceive || releaseBusyId === txnId}
            onClick={() => onRelease(row)}
          >
            Review stock
          </button>
        );
      }
      const value = row[field];
      return value == null || value === "" ? "—" : String(value);
    },
    [canReceive, releaseBusyId, onRelease],
  );

  const renderTxnCell = useCallback((row, field) => {
    if (field === "TxnAt") return formatTxnWhen(row) || "—";
    if (field === "TxnType") return row.TxnType || row.txnType || "—";
    if (field === "Product") return productCell(row);
    if (field === "BatchNumber") return row.BatchNumber || row.batchNumber || "—";
    if (field === "Quantity") {
      const v = row.Quantity ?? row.quantity;
      return v == null ? "—" : String(v);
    }
    if (field === "ReferenceNo") {
      return row.ReferenceNo || row.referenceNo || "—";
    }
    if (field === "ReceiptStatus") {
      const s = row.ReceiptStatus || row.receiptStatus;
      return s ? renderReceiptStatusBadge(s) : "—";
    }
    const value = row[field];
    return value == null || value === "" ? "—" : String(value);
  }, []);

  if (!open) return null;

  return (
    <div
      className="modal fade show d-block epsf-inv-detail-modal"
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-labelledby="epsf-inv-detail-title"
    >
      <div className="modal-dialog epsf-inv-detail-modal__dialog">
        <div className="modal-content epsf-inv-detail-modal__content">
          <div className="modal-header epsf-inv-detail-modal__header">
            <div className="epsf-inv-detail-modal__header-main">
              <div className="epsf-inv-detail-modal__title-row">
                <h5 className="modal-title" id="epsf-inv-detail-title">
                  {title}
                </h5>
                {contextLabel ? (
                  <span className="epsf-inv-detail-modal__context">
                    {contextLabel}
                  </span>
                ) : null}
              </div>
              <div className="epsf-inv-detail-modal__meta-row">
                {(impGroup || filterKey !== "all") && (
                  <button
                    type="button"
                    className="btn btn-link btn-sm epsf-inv-detail-modal__back"
                    onClick={() => {
                      onClearImp?.();
                      onClearFilter?.();
                      onViewChange("batches");
                    }}
                  >
                    All inventory
                  </button>
                )}
                {filterKey && filterKey !== "all" ? (
                  <button
                    type="button"
                    className="epsf-inv-chip"
                    onClick={onClearFilter}
                  >
                    {activeFilterLabel}
                    <span aria-hidden> ×</span>
                  </button>
                ) : null}
                {impGroup ? (
                  <button
                    type="button"
                    className="epsf-inv-chip"
                    onClick={onClearImp}
                  >
                    {impGroup.productCode || impGroup.productName || "IMP"}
                    <span aria-hidden> ×</span>
                  </button>
                ) : null}
              </div>
            </div>
            <button
              ref={closeRef}
              type="button"
              className="btn-close"
              aria-label="Close inventory details"
              onClick={onClose}
            />
          </div>

          <div className="epsf-inv-detail-modal__toolbar">
            <input
              type="search"
              className="form-control epsf-inv-detail-modal__search"
              placeholder="Search IMP, code, batch / lot"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search inventory records"
            />
            <Dropdown
              options={STATUS_OPTIONS}
              selected={findOption(STATUS_OPTIONS, filterKey || "all")}
              onSelect={(opt) => {
                const next = opt?.value || "all";
                onFilterKeyChange?.(next);
                if (next === "held") onViewChange("held");
                else onViewChange("batches");
              }}
            />
            <Dropdown
              options={TRACKING_OPTIONS}
              selected={findOption(TRACKING_OPTIONS, tracking)}
              onSelect={(opt) => setTracking(opt?.value || "")}
            />
            <Dropdown
              options={expiringOptions}
              selected={findOption(expiringOptions, String(days))}
              onSelect={(opt) =>
                setLocalExpiringDays(Number(opt?.value) || days)
              }
            />
            <Dropdown
              options={SORT_OPTIONS}
              selected={findOption(SORT_OPTIONS, sortKey)}
              onSelect={(opt) => setSortKey(opt?.value || "product")}
            />
          </div>

          <div className="epsf-inv-detail-modal__tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={view === "batches"}
              className={`epsf-inv-detail-modal__tab${
                view === "batches" ? " is-active" : ""
              }`}
              onClick={() => onViewChange("batches")}
            >
              Batches
            </button>
            {showKitsTab ? (
              <button
                type="button"
                role="tab"
                aria-selected={view === "kits"}
                className={`epsf-inv-detail-modal__tab${
                  view === "kits" ? " is-active" : ""
                }`}
                onClick={() => onViewChange("kits")}
              >
                Kits
              </button>
            ) : null}
            <button
              type="button"
              role="tab"
              aria-selected={view === "held"}
              className={`epsf-inv-detail-modal__tab${
                view === "held" ? " is-active" : ""
              }`}
              onClick={() => onViewChange("held")}
            >
              Hold / review
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={view === "movements"}
              className={`epsf-inv-detail-modal__tab${
                view === "movements" ? " is-active" : ""
              }`}
              onClick={() => onViewChange("movements")}
            >
              Movement history
            </button>
          </div>

          <div className="modal-body epsf-inv-detail-modal__body">
            {view === "batches" ? (
              <div className="epsf-report-wrap">
                <ReportTable
                  rows={scopedBalances}
                  fieldOptions={BALANCE_FIELD_OPTIONS}
                  defaultColumns={BALANCE_DEFAULT_COLUMNS}
                  getFieldLabel={(field) =>
                    BALANCE_FIELD_OPTIONS.find((x) => x.key === field)?.label ||
                    field
                  }
                  renderCell={renderBalanceCell}
                  getRowKey={(row, index) => batchKey(row) || String(index)}
                  persistLayoutKey="epsf-imp-inv-modal-balances-v4"
                  emptyMessage="No batch balances for this view."
                  pageSizeOptions={[10, 25, 50, 100]}
                  columnSearchMode="client"
                />
              </div>
            ) : null}

            {view === "kits" ? (
              <div className="epsf-report-wrap">
                <ReportTable
                  rows={scopedKits}
                  loading={kitsBusy}
                  fieldOptions={KIT_FIELD_OPTIONS}
                  defaultColumns={KIT_DEFAULT_COLUMNS}
                  getFieldLabel={(field) =>
                    KIT_FIELD_OPTIONS.find((x) => x.key === field)?.label ||
                    field
                  }
                  renderCell={renderKitCell}
                  getRowKey={(row, index) =>
                    String(row.ImpKitId || row.impKitId || index)
                  }
                  persistLayoutKey="epsf-imp-inv-modal-kits-v4"
                  emptyMessage={
                    kitsBusy ? "Loading kits…" : "No kits for this view."
                  }
                  pageSizeOptions={[10, 25, 50, 100]}
                  columnSearchMode="client"
                />
              </div>
            ) : null}

            {view === "held" ? (
              <div className="epsf-report-wrap">
                <ReportTable
                  rows={scopedHeld}
                  fieldOptions={HELD_FIELD_OPTIONS}
                  defaultColumns={HELD_DEFAULT_COLUMNS}
                  getFieldLabel={(field) =>
                    HELD_FIELD_OPTIONS.find((x) => x.key === field)?.label ||
                    field
                  }
                  renderCell={renderHeldCell}
                  getRowKey={(row, index) =>
                    String(row.ImpTxnId || row.impTxnId || index)
                  }
                  persistLayoutKey="epsf-imp-inv-modal-held-v4"
                  emptyMessage="No held receipts or returns for this view."
                  pageSizeOptions={[10, 25, 50, 100]}
                  columnSearchMode="client"
                />
              </div>
            ) : null}

            {view === "movements" ? (
              <div className="epsf-report-wrap">
                <ReportTable
                  rows={scopedMovements}
                  loading={movementsBusy}
                  fieldOptions={TXN_FIELD_OPTIONS}
                  defaultColumns={TXN_DEFAULT_COLUMNS}
                  getFieldLabel={(field) =>
                    TXN_FIELD_OPTIONS.find((x) => x.key === field)?.label ||
                    field
                  }
                  renderCell={renderTxnCell}
                  getRowKey={(row, index) =>
                    String(row.ImpTxnId || row.impTxnId || index)
                  }
                  persistLayoutKey="epsf-imp-inv-modal-txns-v4"
                  emptyMessage={
                    movementsBusy
                      ? "Loading movements…"
                      : "No movements for this view."
                  }
                  pageSizeOptions={[10, 25, 50, 100]}
                  columnSearchMode="client"
                />
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
